import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GetPageAnnotations200, PageAnnotation } from "~/api/model";
import {
  createPageAnnotation,
  deletePageAnnotation,
  getGetPageAnnotationsQueryKey,
  updatePageAnnotation,
  useGetPageAnnotations,
} from "~/api/page-annotations/page-annotations";
import type {
  CreatePageAnnotationBody,
  UpdatePageAnnotationBody,
} from "../types";

export function usePageAnnotations(
  organizationId: string,
  projectId: string | undefined
) {
  const queryClient = useQueryClient();

  const queryKey = getGetPageAnnotationsQueryKey(
    organizationId,
    projectId ?? ""
  );

  const { data, isFetching, isError } = useGetPageAnnotations(
    organizationId,
    projectId ?? "",
    {
      query: {
        enabled: !!organizationId && !!projectId,
        staleTime: 30_000,
      },
    }
  );

  const createMutation = useMutation({
    mutationFn: ({
      pageId,
      body,
    }: {
      pageId: string;
      body: CreatePageAnnotationBody;
    }) => createPageAnnotation(organizationId, projectId ?? "", pageId, body),
    onSuccess: (newAnnotation) => {
      queryClient.setQueryData<GetPageAnnotations200>(queryKey, (old) => ({
        annotations: [newAnnotation, ...(old?.annotations ?? [])],
      }));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      annotationId,
      body,
    }: {
      annotationId: string;
      body: UpdatePageAnnotationBody;
    }) =>
      updatePageAnnotation(organizationId, projectId ?? "", annotationId, body),
    onSuccess: (updatedAnnotation) => {
      queryClient.setQueryData<GetPageAnnotations200>(queryKey, (old) => ({
        annotations: (old?.annotations ?? []).map((a) =>
          a.id === updatedAnnotation.id ? updatedAnnotation : a
        ),
      }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ annotationId }: { annotationId: string }) =>
      deletePageAnnotation(organizationId, projectId ?? "", annotationId),
    onSuccess: (_result, { annotationId }) => {
      queryClient.setQueryData<GetPageAnnotations200>(queryKey, (old) => ({
        annotations: (old?.annotations ?? []).filter(
          (a) => a.id !== annotationId
        ),
      }));
    },
  });

  const annotations = data?.annotations ?? [];

  const getAnnotationsForPage = (pageId: string): PageAnnotation[] =>
    annotations.filter((a) => a.pageId === pageId);

  return {
    annotations,
    getAnnotationsForPage,
    isFetching,
    isError,
    createAnnotation: createMutation.mutateAsync,
    updateAnnotation: updateMutation.mutateAsync,
    deleteAnnotation: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
